import { useAtom } from "jotai";
import { useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../shared/api/yenta-api";
import { AdapexAds } from "../shared/components/adapex-ads.component";
import { ArcadeHeader } from "../shared/components/arcade-header/arcade-header.component";
import { ArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/arcade-instructions-modal.component";
import { useArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/use-arcade-instructions-modal";
import { ArcadeGames } from "../shared/types";
import {
  getArcadeGameImageUrl,
  retrieveFromSessionStorage,
  saveInSessionStorage,
} from "../shared/utils";

import { MatchUpRound } from "./components/round/round.component";
import { matchUpGameAtom } from "./widget.state";

const enable_quiz_ads = window.FKL.enable_quiz_ads;
const show_adapex_ads =
  window.FKL.show_adapex_ads != undefined &&
  window.FKL.show_adapex_ads.toLocaleLowerCase() === "true";

export function Widget() {
  const [matchUpGame] = useAtom(matchUpGameAtom);
  const [
    showInstructionsModal,
    handleHideInstructionsModal,
  ] = useArcadeInstructionsModal(ArcadeGames.MatchUp);
  const handleWidgetError = useErrorHandler();
  const arcadeAvailableGames = window.FKL.arcadeAvailableGamesInfo;

  const matchUpExtraEntries = useMemo(
    () =>
      matchUpGame.rounds
        .map(({ pairs }) => pairs)
        .reduce(
          (acc, pairs) =>
            acc +
            pairs.reduce((acum, pair) => (pair.isMatched ? acum + 1 : acum), 0),
          0
        ),
    [matchUpGame.rounds]
  );

  const sessionEntries = useMemo(() => {
    const SWEEPSTAKE_SESSION_ENTRIES = "sweepstakeSessionEntries";
    const sweepstakeSessionEntries = retrieveFromSessionStorage(
      SWEEPSTAKE_SESSION_ENTRIES
    );
    if (!sweepstakeSessionEntries) {
      saveInSessionStorage(SWEEPSTAKE_SESSION_ENTRIES, 0);
      return 0;
    }
    if (matchUpExtraEntries > 0) {
      saveInSessionStorage(
        SWEEPSTAKE_SESSION_ENTRIES,
        Number(sweepstakeSessionEntries) + 1
      );
      return Number(sweepstakeSessionEntries) + 1;
    }
    return Number(sweepstakeSessionEntries);
  }, [matchUpExtraEntries]);

  const redeemedEntries = useCallback(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return submission ? Number(submission.data.redeemedEntries) || 0 : 0;
  }, []);

  const initialArcadeMatchedPairs = useMemo(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return Number(submission?.data.arcadeMatchedPairs) || 0;
  }, []);

  const arcadeMatchedPairs = useMemo(() => {
    return matchUpGame.rounds
      .map(({ pairs }) => pairs)
      .reduce(
        (acc, pairs) =>
          acc +
          pairs.reduce((acum, pair) => (pair.isMatched ? acum + 1 : acum), 0),
        initialArcadeMatchedPairs
      );
  }, [initialArcadeMatchedPairs, matchUpGame.rounds]);

  useEffect(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    if (submission) {
      const [lastRoundCompleted] = matchUpGame.rounds
        .filter((round) => {
          return round.isFinalized;
        })
        .slice(-1);

      const lastMatchUpRoundId = lastRoundCompleted
        ? lastRoundCompleted.id
        : submission.data.lastMatchUpRoundId;

      const patchData = {
        arcadeMatchedPairs,
        quizExtraEntries:
          matchUpExtraEntries + Number(matchUpGame.allTimePoints!) || 0,
        quizAcceptedCpls: matchUpGame.rounds
          .reduce<string[]>(
            (acc, round) => {
              if (round.cpl) {
                let isAccepted = false;
                if (round.cpl.isAccepted) isAccepted = true;
                if (isAccepted && !acc.includes(round.cpl.name!))
                  return [...acc, round.cpl.name!];
              }
              return acc;
            },
            ((submission.data.quizAcceptedCpls as string) ?? "")
              .split(",")
              .filter((cpl) => {
                return cpl;
              })
          )
          .join(","),
        lastMatchUpRoundId,
      };
      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") ?? "";
      updateFormioSubmission(
        sweepFormUrl,
        {
          ...submission,
          data: {
            ...submission.data,
            ...patchData,
          },
        },
        handleWidgetError
      ).then((submission: any) => {
        window.theme.Sweepstake.prototype.storeSubmission(submission);
      });
    }
  }, [
    matchUpExtraEntries,
    arcadeMatchedPairs,
    matchUpGame.allTimePoints,
    matchUpGame.rounds,
    handleWidgetError,
  ]);

  useLayoutEffect(() => {
    const mainContent = document.querySelector(
      ".main-content"
    ) as HTMLElement | null;
    if (mainContent) mainContent.style.paddingTop = "0px";

    const container = document.querySelector(
      ".product-template__container"
    ) as HTMLElement;
    if (container) container.classList.remove("page-width");
  }, []);

  return (
    <div className="arcade--match-up-game">
      <ArcadeHeader
        allTimeEntries={matchUpGame.allTimePoints!}
        extraEntries={matchUpExtraEntries}
        sessionEntries={sessionEntries}
        redeemedEntries={redeemedEntries()}
      />
      {enable_quiz_ads && show_adapex_ads ? (
        <div className="arcade--matchup__ads arcade--matchup__ads--left">
          <AdapexAds adunit="fkl_300v_Sticky_Left" />
        </div>
      ) : (
        <div />
      )}
      <MatchUpRound showRoundAds={enable_quiz_ads && show_adapex_ads} />
      {enable_quiz_ads && show_adapex_ads ? (
        <div className="arcade--matchup__ads arcade--matchup__ads--right">
          <AdapexAds adunit="fkl_300v_Sticky" />
        </div>
      ) : (
        <div />
      )}

      <ArcadeInstructionsModal
        isOpen={showInstructionsModal}
        title="Product Matchup"
        subtitle=""
        image={getArcadeGameImageUrl(
          arcadeAvailableGames,
          document.location.href
        )}
        instructions={
          <>
            Match products to win points. You will have{" "}
            <strong>1 minute</strong> to make matches, with a total number of{" "}
            <strong>8 pairs per round.</strong> Good luck!
          </>
        }
        onHideInstructionsModal={handleHideInstructionsModal}
      />
    </div>
  );
}

declare global {
  interface FKL {
    matchUpSlug: string;
    arcadeApiUrl: string;
    assetWidgetUrl: string;
    enable_quiz_ads: boolean;
    show_adapex_ads: string;
  }
}
